import { ReactNode } from 'react';
import { ComponentKey } from 'enums/componentKey';
import { ComponentConfig, ConfigFor, DynamicArrayField, DynamicArrayFieldFor } from 'types/form/componentConfig';

export type Translate = (key: string, options?: Record<string, any>) => string;

/** What every renderer receives besides its config: the translator and the
    form state (for dynamic disabled rules). */
export interface FieldContext {
  t: Translate;
  state?: any;
}

/** The common props, resolved ONCE before dispatch: translated labels,
    evaluated disabled rule. */
export interface ResolvedBase {
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

/** A renderer receives its config already narrowed by the discriminated union. */
export type InputRenderer<K extends ComponentKey> = (
  config: ConfigFor<K>,
  base: ResolvedBase,
  ctx: FieldContext,
) => ReactNode;

/** The complete registry: one renderer per ComponentKey. Assembled at the
    composition root (src/index.tsx); a missing key is a compile error. */
export type InputRenderers = { [K in ComponentKey]: InputRenderer<K> };

/** The subset a module contributes (the core, or one feature). */
export type InputRenderersFor<K extends ComponentKey> = Pick<InputRenderers, K>;

/* ---- DynamicArray row sub-fields ----
   A different contract from form renderers: rows are controlled (value +
   onChange through arrayHelpers), not bound to Formik by name. The registry
   works the same way: the core knows no feature component, features
   register theirs. */
export interface RowFieldContext {
  value: any;
  onChange: (value: any) => void;
  /** Already resolved by DynamicArray (hidden in compact mode). */
  label?: string;
  required?: boolean;
}

export type RowKey = DynamicArrayField['componentKey'];

export type RowInputRenderer<K extends RowKey> = (
  field: DynamicArrayFieldFor<K>,
  ctx: RowFieldContext,
) => ReactNode;

export type RowInputRenderers = { [K in RowKey]: RowInputRenderer<K> };

export type RowInputRenderersFor<K extends RowKey> = Pick<RowInputRenderers, K>;

export const resolveBase = (
  config: ComponentConfig,
  t: Translate,
  state: any,
): ResolvedBase => ({
  name: config.name,
  label: config.label ? t(config.label) : undefined,
  placeholder: config.placeholder ? t(config.placeholder) : undefined,
  required: config.required,
  disabled: typeof config.disabled === 'function' ? config.disabled(state) : config.disabled,
});

export const translateItems = <I extends { label: string }>(items: I[], t: Translate): I[] =>
  items.map(item => ({ ...item, label: t(item.label) }));
